import React, { useMemo, useState } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  TextField,
} from "@mui/material";
import Draggable from "react-draggable";
import apiUtils from "../../utils/apiUtils";

const validationSchema = Yup.object().shape({
  supplierName: Yup.string().required("Required"),
  supplierStreet: Yup.string().required("Required"),
  supplierBuildingIdentifier: Yup.string().required("Required"),
  supplierPostalCode: Yup.string().required("Required"),
  supplierCity: Yup.string().required("Required"),
  countryCode: Yup.number().typeError("Required").required("Required"),
  supplierVATnr: Yup.string().required("Required"),
  supplierCode: Yup.number()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .nullable()
    .integer("Use a whole number"),
});

const normalizeSupplierCode = (value) => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) {
    return null;
  }

  return parsed;
};

const getNextSupplierCode = (suppliers) => {
  const highestCode = suppliers.reduce((maxCode, supplier) => {
    const currentCode = normalizeSupplierCode(supplier?.supplierCode);
    if (currentCode === null) {
      return maxCode;
    }

    return Math.max(maxCode, currentCode);
  }, 0);

  return highestCode + 1;
};

const buildInitialValues = (defaultCountryCode, nextSupplierCode) => ({
  supplierName: "",
  supplierStreet: "",
  supplierBuildingIdentifier: "",
  supplierPostalCode: "",
  supplierCity: "",
  supplierProvince: "",
  countryCode: defaultCountryCode || "",
  supplierCoC: "",
  supplierVATnr: "",
  supplierCode: String(nextSupplierCode),
});

const DraggableDialogPaper = (props) => {
  const draggableNodeRef = React.useRef(null);

  return (
    <Draggable nodeRef={draggableNodeRef} handle="#draggable-create-supplier-title">
      <Paper {...props} ref={draggableNodeRef} />
    </Draggable>
  );
};

const CreateSupplierDialog = ({ open, onClose, onCreated, defaultCountryCode, suppliers = [] }) => {
  const countryData = apiUtils.useApiData("/api/v1/countries");
  const [submitError, setSubmitError] = useState("");

  const nextSupplierCode = useMemo(() => getNextSupplierCode(suppliers), [suppliers]);
  const initialValues = useMemo(
    () => buildInitialValues(defaultCountryCode, nextSupplierCode),
    [defaultCountryCode, nextSupplierCode]
  );
  const countryLocked = Boolean(defaultCountryCode);

  const checkSupplierCodeAvailability = async (supplierCode, useLatestServerData = false) => {
    if (supplierCode === null) {
      return true;
    }

    if (!useLatestServerData) {
      return !suppliers.some((supplier) => Number(supplier.supplierCode) === supplierCode);
    }

    const latestSuppliersResponse = await axios.get("/api/v1/suppliers");
    const latestSuppliers = latestSuppliersResponse.data ?? [];
    return !latestSuppliers.some((supplier) => Number(supplier.supplierCode) === supplierCode);
  };

  const handleSubmit = async (values, { setSubmitting, resetForm, setFieldError }) => {
    setSubmitError("");

    try {
      const manualCode = normalizeSupplierCode(values.supplierCode);
      const hasManualCode = values.supplierCode !== "" && values.supplierCode !== null && values.supplierCode !== undefined;
      const preferredSupplierCode = manualCode ?? nextSupplierCode;
      const latestSuppliersResponse = await axios.get("/api/v1/suppliers");
      const latestSuppliers = latestSuppliersResponse.data ?? [];
      const codeExists = latestSuppliers.some((supplier) => Number(supplier.supplierCode) === preferredSupplierCode);

      if (hasManualCode && codeExists) {
        setFieldError("supplierCode", "Supplier code already exists.");
        setSubmitting(false);
        return;
      }

      const finalSupplierCode = codeExists ? getNextSupplierCode(latestSuppliers) : preferredSupplierCode;

      const payload = {
        supplierName: values.supplierName,
        supplierStreet: values.supplierStreet,
        supplierBuildingIdentifier: values.supplierBuildingIdentifier,
        supplierPostalCode: values.supplierPostalCode,
        supplierCity: values.supplierCity,
        supplierProvince: values.supplierProvince,
        countryCode: Number(values.countryCode),
        supplierCoC: values.supplierCoC,
        supplierVATnr: values.supplierVATnr,
        supplierCode: finalSupplierCode,
      };

      const response = await axios.post("/api/v1/suppliers", payload);
      resetForm({ values: buildInitialValues(defaultCountryCode, finalSupplierCode + 1) });
      onCreated(response.data);
    } catch (error) {
      setSubmitError(error?.response?.data || error.message || "Failed to create supplier.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick") {
          return;
        }

        onClose();
      }}
      fullWidth
      maxWidth="md"
      PaperComponent={DraggableDialogPaper}
      PaperProps={{
        sx: {
          pointerEvents: "auto",
          userSelect: "none",
          "& .MuiInputBase-input": {
            userSelect: "text",
          },
        },
      }}
      hideBackdrop
      disableScrollLock
      disableEnforceFocus
      disableAutoFocus
      disableRestoreFocus
      sx={{
        pointerEvents: "none",
      }}
    >
      <DialogTitle id="draggable-create-supplier-title" sx={{ cursor: "move" }}>
        Add Supplier
      </DialogTitle>
      <DialogContent dividers>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            errors,
            touched,
            values,
            isSubmitting,
            handleBlur,
            handleChange,
            handleSubmit: handleFormSubmit,
            setFieldError,
          }) => (
            <Box component="form" onSubmit={handleFormSubmit} sx={{ pt: 1 }}>
              {submitError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {submitError}
                </Alert>
              ) : null}

              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    name="supplierName"
                    label="Supplier Name"
                    value={values.supplierName}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={Boolean(touched.supplierName && errors.supplierName)}
                    helperText={touched.supplierName && errors.supplierName}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    name="supplierCode"
                    label="Supplier Code"
                    value={values.supplierCode}
                    onBlur={async (event) => {
                      handleBlur(event);

                      if (values.supplierCode === "" || values.supplierCode === null || values.supplierCode === undefined) {
                        setFieldError("supplierCode", undefined);
                        return;
                      }

                      const normalizedCode = normalizeSupplierCode(values.supplierCode);
                      if (normalizedCode === null) {
                        setFieldError("supplierCode", "Use a valid whole number.");
                        return;
                      }

                      const isAvailable = await checkSupplierCodeAvailability(normalizedCode, true);
                      if (!isAvailable) {
                        setFieldError("supplierCode", "Supplier code already exists.");
                        return;
                      }

                      setFieldError("supplierCode", undefined);
                    }}
                    onChange={handleChange}
                    error={Boolean(touched.supplierCode && errors.supplierCode)}
                    helperText={touched.supplierCode && errors.supplierCode ? errors.supplierCode : ""}
                  />
                </Grid>
                <Grid item xs={12} md={7}>
                  <TextField
                    fullWidth
                    name="supplierStreet"
                    label="Supplier Street"
                    value={values.supplierStreet}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={Boolean(touched.supplierStreet && errors.supplierStreet)}
                    helperText={touched.supplierStreet && errors.supplierStreet}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    name="supplierBuildingIdentifier"
                    label="Building Number"
                    value={values.supplierBuildingIdentifier}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={Boolean(touched.supplierBuildingIdentifier && errors.supplierBuildingIdentifier)}
                    helperText={touched.supplierBuildingIdentifier && errors.supplierBuildingIdentifier}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    name="supplierPostalCode"
                    label="Postal Code"
                    value={values.supplierPostalCode}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={Boolean(touched.supplierPostalCode && errors.supplierPostalCode)}
                    helperText={touched.supplierPostalCode && errors.supplierPostalCode}
                  />
                </Grid>
                <Grid item xs={12} md={5}>
                  <TextField
                    fullWidth
                    name="supplierCity"
                    label="City"
                    value={values.supplierCity}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={Boolean(touched.supplierCity && errors.supplierCity)}
                    helperText={touched.supplierCity && errors.supplierCity}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    name="supplierProvince"
                    label="Province"
                    value={values.supplierProvince}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    select
                    fullWidth
                    name="countryCode"
                    label="Country"
                    value={values.countryCode}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    disabled={countryLocked}
                    error={Boolean(touched.countryCode && errors.countryCode)}
                    helperText={
                      (touched.countryCode && errors.countryCode) ||
                      (countryLocked ? "Country is locked to the selected draft." : "")
                    }
                  >
                    {countryData.map((country) => (
                      <MenuItem key={country.countryId} value={country.countryId}>
                        {country.countryCode} - {country.countryName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="supplierCoC"
                    label="Chamber of Commerce"
                    value={values.supplierCoC}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="supplierVATnr"
                    label="VAT Number"
                    value={values.supplierVATnr}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={Boolean(touched.supplierVATnr && errors.supplierVATnr)}
                    helperText={touched.supplierVATnr && errors.supplierVATnr}
                  />
                </Grid>
              </Grid>

              <DialogActions sx={{ px: 0, pt: 3 }}>
                <Button type="button" onClick={onClose} disabled={isSubmitting}>
                  Close
                </Button>
                <Button type="submit" variant="contained" disabled={isSubmitting}>
                  {isSubmitting ? <CircularProgress size={18} /> : "Create Supplier"}
                </Button>
              </DialogActions>
            </Box>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default CreateSupplierDialog;
